import { useState } from 'react';
import { Box, Button, Card, Stack, TextField, Typography } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { payApiClient } from '@/api/client';
import { ApiRequestError } from '@/api/errors';
import type { AdminGatewayAccount, TestBenchPaymentLaunchResponse } from '@/api/models';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { ErrorState } from '@/components/shell/ErrorState';
import { FilterableSelect } from '@/components/form/FilterableSelect';
import { tenantToOption } from '@/components/form/optionMappers';
import { LoadingState } from '@/components/shell/LoadingState';
import { useApiResource } from '@/hooks/useApiResource';
import { formatMinorUnits } from '@/utils/amount';
import { TestCardPanel } from './TestCardPanel';

const DEFAULT_AMOUNT_MINOR = 100;
const CURRENCY = 'EUR';

interface TestBenchLaunchFormProps {
  canWrite: boolean;
  onLaunched: (intentId: string, orderReference: string) => void;
}

/**
 * Launch form against `POST /v1/admin/test-bench/payments`. Read-only for
 * `platform:auditor` (`canWrite` false) — the fields still show the current
 * selection but every control is disabled and no Launch button renders; the
 * API's own `access_denied` boundary is never relied upon as the only guard,
 * but it is also never bypassed if this client-side gate were ever wrong.
 */
export function TestBenchLaunchForm({ canWrite, onLaunched }: TestBenchLaunchFormProps) {
  const translate = useTranslate();
  const notify = useNotify();
  const [tenantCode, setTenantCode] = useState('');
  const [gatewayAccountId, setGatewayAccountId] = useState('');
  const [amountMinorInput, setAmountMinorInput] = useState(String(DEFAULT_AMOUNT_MINOR));
  const [launching, setLaunching] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [result, setResult] = useState<TestBenchPaymentLaunchResponse>();

  const tenants = useApiResource(() => payApiClient.listAdminTenants({ page: 1, pageSize: 200 }), 'test-bench-tenants');
  const gatewayAccounts = useApiResource(
    () =>
      tenantCode
        ? payApiClient.listAdminGatewayAccounts({ tenantCode: [tenantCode], page: 1, pageSize: 200 })
        : Promise.resolve({ items: [] as AdminGatewayAccount[], page: { page: 1, pageSize: 200, totalItems: 0, totalPages: 0 } }),
    `test-bench-gateway-accounts-${tenantCode}`,
  );

  const sortedAccounts = [...(gatewayAccounts.data?.items ?? [])].sort((a, b) => {
    if (a.mode === b.mode) return 0;
    return a.mode === 'test' ? -1 : 1;
  });

  // This screen only ever renders once the test bench is known available
  // (`TestBenchPage` gates on it), so fetching the preset catalog here needs
  // no extra availability guard of its own.
  const presets = useApiResource(() => payApiClient.getTestBenchPresets(), 'test-bench-launch-presets');
  const selectedAccount = sortedAccounts.find((account) => account.id === gatewayAccountId);
  const selectedTestCard = selectedAccount?.presetCode
    ? presets.data?.find((preset) => preset.code === selectedAccount.presetCode)?.testCard
    : undefined;

  function handleTenantChange(nextTenantCode: string) {
    setTenantCode(nextTenantCode);
    setGatewayAccountId('');
    setResult(undefined);
  }

  const amountMinor = Number(amountMinorInput);
  const canSubmit =
    canWrite &&
    tenantCode.trim().length > 0 &&
    gatewayAccountId.trim().length > 0 &&
    Number.isInteger(amountMinor) &&
    amountMinor > 0 &&
    !launching;

  async function handleLaunch() {
    setLaunching(true);
    setAccessDenied(false);
    try {
      const response = await payApiClient.launchTestBenchPayment({
        tenantCode,
        gatewayAccountId,
        amountMinor,
        currency: CURRENCY,
      });
      setResult(response);
      onLaunched(response.intentId, response.orderReference);
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.errorCode === 'access_denied') {
        setAccessDenied(true);
      } else {
        const message = caught instanceof ApiRequestError ? caught.message : translate('pay.errorState.body');
        notify(message, { type: 'error' });
      }
    } finally {
      setLaunching(false);
    }
  }

  if (accessDenied) {
    return (
      <Card>
        <AccessDenied />
      </Card>
    );
  }

  return (
    <Card sx={{ p: 2.5 }}>
      <Stack spacing={2.5} sx={{ maxWidth: 480 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {translate('pay.testBench.launch.title')}
        </Typography>

        {!canWrite && (
          <Typography variant="body2" color="text.secondary">
            {translate('pay.testBench.launch.readOnlyNotice')}
          </Typography>
        )}

        {tenants.loading ? (
          <LoadingState variant="list" rows={2} />
        ) : tenants.error ? (
          <ErrorState body={tenants.error.message} onRetry={tenants.reload} />
        ) : (
          <FilterableSelect
            disabled={!canWrite}
            label={translate('pay.testBench.launch.fieldTenant')}
            options={(tenants.data?.items ?? []).map(tenantToOption)}
            value={tenantCode}
            onChange={handleTenantChange}
          />
        )}

        <FilterableSelect
          disabled={!canWrite || !tenantCode || gatewayAccounts.loading}
          loading={Boolean(tenantCode) && gatewayAccounts.loading}
          label={translate('pay.testBench.launch.fieldGatewayAccount')}
          helperText={
            tenantCode && !gatewayAccounts.loading && sortedAccounts.length === 0
              ? translate('pay.testBench.launch.noAccountsForTenant')
              : translate('pay.testBench.launch.fieldGatewayAccountHelp')
          }
          options={sortedAccounts.map((account) => ({
            value: account.id,
            label: account.alias,
            secondary: `${translate(`pay.gatewayAccounts.gateway.${account.gateway}`)} · ${translate(`pay.gatewayAccounts.mode.${account.mode}`)}`,
          }))}
          value={gatewayAccountId}
          onChange={setGatewayAccountId}
        />

        <TextField
          size="small"
          type="number"
          disabled={!canWrite}
          label={translate('pay.testBench.launch.fieldAmount')}
          value={amountMinorInput}
          onChange={(event) => setAmountMinorInput(event.target.value)}
          helperText={`${translate('pay.testBench.launch.fieldAmountHelp')} ${formatMinorUnits(Number.isFinite(amountMinor) ? amountMinor : 0, CURRENCY)}`}
          fullWidth
        />

        <TextField size="small" disabled label={translate('pay.testBench.launch.fieldCurrency')} value={CURRENCY} fullWidth />

        {selectedTestCard && <TestCardPanel testCard={selectedTestCard} />}

        {canWrite && (
          <Stack direction="row">
            <Button variant="contained" onClick={handleLaunch} disabled={!canSubmit}>
              {translate('pay.testBench.launch.submit')}
            </Button>
          </Stack>
        )}

        {result && (
          <Box sx={{ borderRadius: 1.5, p: 2, bgcolor: 'action.hover' }}>
            <Stack spacing={1.5}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {translate('pay.testBench.result.title')}
              </Typography>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {translate('pay.testBench.result.fieldOrderReference')}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: (theme) => theme.custom.fontFamilyMono, fontWeight: 500 }}>
                  {result.orderReference}
                </Typography>
              </Stack>
              <Button
                variant="contained"
                color="primary"
                component="a"
                href={result.sessionUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {translate('pay.testBench.result.openSessionAction')}
              </Button>
            </Stack>
          </Box>
        )}
      </Stack>
    </Card>
  );
}
